import { cloneElement, isValidElement, useId, useRef } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { day } from '../format.js';

/**
 * A very small set of shared primitives.
 *
 * Deliberately not a component library: this is a handful of internal screens,
 * and consistent spacing plus one button style covers most of what a design
 * system would give us here.
 */

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-brand-on hover:bg-brand-700 disabled:bg-brand-600/50',
  secondary:
    'bg-surface text-body ring-1 ring-inset ring-line-strong hover:bg-sunken disabled:text-faint',
  danger: 'bg-danger-text text-surface hover:opacity-90 disabled:opacity-50',
  ghost: 'text-muted hover:bg-raised disabled:text-faint',
};

export function Button({
  variant = 'primary',
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed ${BUTTON_STYLES[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * Form controls state their background explicitly. Left to the browser, a
 * native control picks its own colors from the platform theme, which is how a
 * dark page ends up with a white select whose options cannot be read.
 */
const CONTROL_CLASS =
  'w-full rounded-md border-0 bg-surface px-3 py-2 text-sm text-strong ring-1 ring-inset ring-line-strong focus:ring-2 focus:ring-inset focus:ring-brand-600 disabled:bg-sunken disabled:text-muted';

/** `ref` is an ordinary prop in React 19: it reaches the input for a page that manages focus. */
export function Input({
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  return <input className={`${CONTROL_CLASS} placeholder:text-faint ${className}`} {...props} />;
}

/**
 * A plain native dropdown. Fine for the short fixed lists this phase has (a
 * role); a type-to-filter box is worth adding the day a list of SKUs lands
 * here.
 */
export function Select({
  className = '',
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={`${CONTROL_CLASS} ${className}`} {...props}>
      {children}
    </select>
  );
}

export function Textarea({
  className = '',
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`${CONTROL_CLASS} placeholder:text-faint ${className}`} {...props} />;
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  // Generated here so no caller has to invent an id for every input in the app.
  const hintId = useId();

  return (
    <div className="block">
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-body">{label}</span>
        {hint ? describedBy(children, hintId) : children}
      </label>
      {hint && (
        <span id={hintId} className="mt-1 block text-xs text-muted">
          {hint}
        </span>
      )}
    </div>
  );
}

/**
 * Points a control at its hint instead of folding the hint into its name.
 *
 * A hint inside the `<label>` becomes part of the control's accessible name,
 * so the code box on the two-factor page would announce itself as "Code You
 * can also enter one of your recovery codes…". `aria-describedby` is what a
 * hint is — read after the name, not as part of it.
 *
 * A control that already names its own description keeps it.
 */
function describedBy(children: ReactNode, hintId: string): ReactNode {
  if (!isValidElement<{ 'aria-describedby'?: string }>(children)) return children;
  if (children.props['aria-describedby']) return children;
  return cloneElement(children, { 'aria-describedby': hintId });
}

export function Card({
  children,
  className = '',
  testId,
}: {
  children: ReactNode;
  className?: string;
  /** A `data-testid` for the browser smoke suite. */
  testId?: string;
}) {
  return (
    <div className={`rounded-lg border border-line bg-surface ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

/**
 * Wraps a table, or anything else that can end up wider than its card, so the
 * scrollbar shows up here instead of on the page. The card's own border and
 * background stay put at the width the card was given; only this strip
 * scrolls, which is what keeps a wide table from pushing its background past
 * the card and the whole page wider than the viewport.
 */
export function TableScroll({ children }: { children: ReactNode }) {
  return <div className="overflow-x-auto">{children}</div>;
}

type AlertTone = 'error' | 'warning' | 'info' | 'success';

const ALERT_STYLES: Record<AlertTone, string> = {
  error: 'bg-danger-bg text-danger-text ring-danger-line',
  warning: 'bg-warn-bg text-warn-text ring-warn-line',
  info: 'bg-sunken text-body ring-line',
  success: 'bg-ok-bg text-ok-text ring-ok-line',
};

export function Alert({ tone = 'error', children }: { tone?: AlertTone; children: ReactNode }) {
  return (
    <div className={`rounded-md px-3 py-2 text-sm ring-1 ring-inset ${ALERT_STYLES[tone]}`}>
      {children}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted">
      <span
        className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-body"
        aria-hidden
      />
      {label}
    </div>
  );
}

export interface TabItem {
  id: string;
  label: ReactNode;
}

/**
 * A horizontal tab bar. Real `role="tab"` buttons with roving focus and arrow
 * -key navigation, wrapping to a second line on a narrow window so nothing is
 * ever clipped. The panel each tab controls is rendered by the caller with
 * `role="tabpanel"`.
 */
export function Tabs({
  items,
  active,
  onChange,
}: {
  items: TabItem[];
  active: string;
  onChange: (id: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = items.findIndex((item) => item.id === active);
    if (index < 0) return;
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % items.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const nextId = items[next]?.id;
    if (!nextId) return;
    onChange(nextId);
    const button = ref.current?.querySelector<HTMLElement>(`[data-tab-id="${nextId}"]`);
    button?.focus();
  }

  return (
    <div ref={ref} role="tablist" onKeyDown={onKeyDown} className="flex flex-wrap gap-1">
      {items.map((item) => {
        const selected = item.id === active;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            data-tab-id={item.id}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              selected ? 'bg-brand-600 text-brand-on' : 'text-muted hover:bg-raised hover:text-body'
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The one line that says where a table's on-hand quantities came from and as
 * of when. The Manage FBA Inventory download is the real FBA census; when all
 * there is is the listing report, the note says so and how to fix it.
 */
export function StockNote({
  source,
  asOf,
  className = '',
}: {
  source: 'fba_inventory' | 'snapshot' | 'listing';
  asOf: string | null;
  className?: string;
}) {
  if (source === 'listing') {
    return (
      <span className={`text-muted ${className}`}>
        Stock from the listing report — upload a Manage FBA Inventory file for real FBA stock.
      </span>
    );
  }
  const label = source === 'fba_inventory' ? 'Manage FBA Inventory' : 'latest inventory snapshot';
  return (
    <span className={`text-muted ${className}`}>
      Stock as of {day(asOf)} ({label})
    </span>
  );
}

/** A page title with the one-line description every screen carries under it. */
export function PageHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div>
      <h1 className="text-lg font-semibold tracking-tight text-strong">{title}</h1>
      {description && <p className="mt-1 text-sm text-muted">{description}</p>}
    </div>
  );
}
